import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { SupTransactionType, SupWeb3Balance, SupWeb3DepositStep, SupWeb3TxSingleVault, SupWeb3WithdrawSingleVault } from "@/types";
import React, { Dispatch, Fragment, type SetStateAction, useCallback, useMemo, useState } from "react";

import config from "@/../config/dapp.config";
import dictionaryConfig from "@/../config/dictionary.config";

import ChipVaultAssets from "@/components/Ui/Chip/Vault/Assets";
import UiTooltip from "@/components/Ui/Tooltip";
import { useRouteState } from "@/components/Web3/Common/Route/State";
import { getWeb3TxRoutes } from "@/components/Web3/Common/Route/utils/getWeb3TxRoutes";
import { useSimulationState } from "@/components/Web3/Common/Simulation/State";
import Web3CommonVaultChecks from "@/components/Web3/Common/Vault/Checks";
import Web3CommonVaultMultiStep from "@/components/Web3/Common/Vault/MultiStep";
import Web3CommonVaultRouteContainer from "@/components/Web3/Common/VaultRouteContainer";
import Web3DepositAmount from "@/components/Web3/Deposit/Amount";

import Web3WithdrawAmountWrapper from "@/components/Web3/Withdraw/Amount/Wrapper";
import useRewardSettings from "@/hooks/data/useRewardSettings";
import { useStoredDataDispatch } from "@/store/Context";

import {
  SupLoader,
  SupText,
  SupTooltip,
  SupAlert2,
  SupCheck,
  SupFlag,
  SupRouteError,
  SupTrash,
  SupAccordion,
  SupAccordionDetails,
  SupAccordionSummary,
  SupAccordionValidation,
} from "@repo/core-ui";

import { formatBigNumberish, getNetVaultApy, getProtocolImage, getProtocolName, getVaultApy, getVaultDecimals, roundBigNumberValueString } from "@/utils";

import { twMerge } from "tailwind-merge";

interface Web3CommonVaultSingleProps {
  singleVault: SupWeb3TxSingleVault;
  selectedBalance?: SupWeb3Balance;
  vaultList: SupWeb3TxSingleVault[];
  step: SupWeb3DepositStep;
  setVaultList: Dispatch<SetStateAction<SupWeb3TxSingleVault[]>>;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  type?: SupTransactionType;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3CommonVaultSingle({
  singleVault,
  selectedBalance,
  vaultList,
  setVaultList,
  setStep,
  step,
  type = "deposit",
  calculatedRoutes,
}: Web3CommonVaultSingleProps) {
  const { getVaultSelectedRoute } = useRouteState();
  const { getVaultState } = useSimulationState();

  const storedDispatch = useStoredDataDispatch();
  const rewards = useRewardSettings();

  const vaultId = singleVault.id;

  const vaultState = useMemo(() => getVaultState?.(vaultId), [getVaultState, vaultId]);

  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const { selectedRoute, error } = useMemo(() => {
    const vaultRouteData = getVaultSelectedRoute?.(vaultId);
    return {
      selectedRoute: vaultRouteData?.selectedRoute,
      error: vaultRouteData?.error,
    };
  }, [getVaultSelectedRoute, vaultId]);

  const errorMessage = useMemo(() => {
    return error?.message;
  }, [error]);

  const titleLimit = step === "Default" ? 25 : 12;

  const { calculatedRoute: route } = useMemo(() => getWeb3TxRoutes(singleVault.id, calculatedRoutes), [calculatedRoutes, singleVault.id]);

  const superPositionAmount = useMemo(() => {
    if (!route || !selectedRoute) {
      return undefined;
    }

    if (selectedRoute === "direct") {
      return route.routes[0] ? route.routes[0].super_position : undefined;
    }

    return selectedRoute.superPosition;
  }, [route, selectedRoute]);

  /**
   * Destination swap flag applied to the multivault deposits only
   */
  const hasDstSwap = useMemo(() => {
    return (vaultList.length > 0 && route?.in.has_dst_swap) || false;
  }, [route?.in.has_dst_swap, vaultList.length]);

  const removeCallback = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      storedDispatch({ type: type === "deposit" ? "toggleCart" : "removeWithdrawCart", vault: singleVault });
    },
    [singleVault, storedDispatch, type]
  );

  return (
    <SupAccordion
      variant={errorMessage !== undefined ? "error" : "primary"}
      onChange={expanded => setIsExpanded(expanded)}
      className={twMerge("rounded-bl-lg border border-neutral-200 bg-neutral-25", isExpanded && "bg-neutral-25")}
    >
      <SupAccordionSummary
        className={twMerge("min-h-[60px] w-full overflow-hidden rounded-bl-lg bg-neutral-25", isExpanded && "[&.active]:bg-neutral-75")}
        iconClassName={twMerge(
          "rounded-full transition-colors text-neutral-900 duration-200 mr-3 border-[.5px] border-primary-500",
          errorMessage !== undefined ? "bg-error-50 hover:bg-error-100" : "bg-primary-25 hover:bg-primary-50"
        )}
      >
        <div className="group relative flex items-center justify-between py-3 pl-2 pr-1.5">
          {vaultState && (
            <SupTooltip color="primary" content={`Vault simulation status: ${vaultState.success ? "Success" : vaultState.loading ? "In progress" : "Failed"}`}>
              <div className="absolute left-0.5 top-0.5 z-10 flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full">
                {vaultState.success === false && <SupRouteError className="bg-error-700 text-neutral" />}
                {vaultState.loading === true && <SupLoader isSmall />}
                {vaultState.success === true && <SupCheck className="rounded-full border-0.5 border-neutral bg-success-600 text-neutral" />}
              </div>
            </SupTooltip>
          )}

          {/* Vault Tag */}
          <div className="inline-flex shrink items-center justify-start gap-2">
            <div className="flex w-[42px] items-center justify-center">
              <ChipVaultAssets assets={singleVault.assets} vaultId={singleVault.id} chain={singleVault.chain} useSmall showChain />
            </div>
            <div className="inline-flex shrink grow basis-0 flex-col items-start justify-start overflow-hidden">
              <UiTooltip enable={singleVault.friendly_name.length > titleLimit} content={singleVault.friendly_name} arrowPlacement="top">
                <SupText variant="subtitle1" className="overflow-hidden text-ellipsis whitespace-nowrap">
                  {singleVault.friendly_name}
                </SupText>
              </UiTooltip>
              <div className="mt-1 flex min-w-0 items-center gap-1">
                {singleVault.protocol && (
                  <Fragment>
                    <img className="h-4 w-4 shrink-0 rounded-full" src={getProtocolImage(singleVault)} alt={getProtocolName(singleVault)} />
                    <SupText variant="caption" weight="bold" className="shrink-0">
                      {getProtocolName(singleVault)}
                    </SupText>
                  </Fragment>
                )}
                {getVaultApy(singleVault) !== dictionaryConfig.statsDefaultValue && (
                  <SupText as="div" variant="body2" className="overflow-hidden text-ellipsis whitespace-nowrap">
                    {parseFloat(String(getNetVaultApy(singleVault, rewards, undefined, true))).toFixed(config.decimalsApyRoundingLimit)}%
                  </SupText>
                )}
              </div>
            </div>
          </div>

          {/* Vault Amount */}
          {selectedBalance && singleVault.amount && !(typeof singleVault.amount === "bigint" ? singleVault.amount === 0n : singleVault.amount.isZero()) && (
            <div className="ml-auto flex-auto shrink-0">
              {calculatedRoutes.isLoading ? (
                <SupLoader isSmall />
              ) : (
                superPositionAmount && (
                  <SupText variant="button" weight="bold" className="text-right">
                    {formatBigNumberish(superPositionAmount.amount, getVaultDecimals(singleVault)).toString()}
                    <span className="ml-0.5 inline-flex max-w-[60px] overflow-hidden text-clip whitespace-nowrap">{superPositionAmount.symbol.replace("SUP-", "")}</span>
                  </SupText>
                )
              )}
              {superPositionAmount && (
                <SupText variant="body2" weight="bold" className="mt-1 text-right">
                  ${roundBigNumberValueString(superPositionAmount.usd_value, config.decimalsRoundingLimit)}
                </SupText>
              )}
            </div>
          )}

          {/* Destination swap flag */}
          {hasDstSwap && (
            <UiTooltip arrowPlacement="left" color="warning" content="Requires a swap on the destination chain">
              <div className="shrink-0 px-2 text-warning-600">
                <SupFlag width={20} height={20} aria-hidden="true" />
              </div>
            </UiTooltip>
          )}

          {/* Vault remove button */}
          <button
            type="button"
            className={twMerge("mr-2 shrink-0 px-1 text-neutral-900 hover:text-gray-500", !isExpanded && "invisible group-hover:visible")}
            onClick={removeCallback}
          >
            <span className="sr-only">Close</span>
            <SupTrash width={20} height={20} aria-hidden="true" />
          </button>

          {/* Vault with recommend_vault_share_withdrawal  */}
          {singleVault.recommend_vault_share_withdrawal && (
            <div className="absolute -right-6 top-0 inline-flex h-3 w-fit items-center justify-start gap-1 whitespace-nowrap rounded-bl-lg border-[.5px] border-neutral-200 bg-primary-75 px-1 text-neutral-800">
              <SupAlert2 className="h-3 w-3" />
              <SupText variant="caption" className="text-[10px]">
                Multi-Step Vault
              </SupText>
            </div>
          )}
        </div>
      </SupAccordionSummary>
      <SupAccordionDetails className="pl-2 pr-3">
        {!selectedBalance && isExpanded && <Web3CommonVaultChecks singleVault={singleVault} type={type} />}

        {selectedBalance && (
          <div className="flex w-full flex-col justify-between">
            {type === "deposit" ? (
              <Web3DepositAmount className="bg-neutral-25" selectedBalance={selectedBalance} singleVault={singleVault} vaultList={vaultList} setVaultList={setVaultList} />
            ) : (
              <Web3WithdrawAmountWrapper
                selectedBalance={selectedBalance}
                singleVault={singleVault as SupWeb3WithdrawSingleVault}
                vaultList={vaultList as SupWeb3WithdrawSingleVault[]}
                setVaultList={setVaultList as Dispatch<SetStateAction<SupWeb3WithdrawSingleVault[]>>}
              />
            )}

            {singleVault.recommend_vault_share_withdrawal && <Web3CommonVaultMultiStep type={type} />}

            {singleVault.amount && !(typeof singleVault.amount === "bigint" ? singleVault.amount === 0n : singleVault.amount.isZero()) && (
              <Web3CommonVaultRouteContainer calculatedRoutes={calculatedRoutes} singleVault={singleVault} setStep={setStep} type={type} />
            )}
          </div>
        )}
      </SupAccordionDetails>
      {errorMessage && (
        <SupAccordionValidation className={twMerge(isExpanded && "border")}>
          {error?.message && (
            <SupText variant="caption" className="p-1">
              {error.message}
            </SupText>
          )}
          {vaultState && !vaultState.success && !error?.message && (
            <SupText variant="caption" className="p-1">
              {vaultState.error}
            </SupText>
          )}
        </SupAccordionValidation>
      )}
    </SupAccordion>
  );
}
